// ---- the tray (DOM: browser-shaped Arabic, no texture memory, reflows for free) ----
// The round's picture at the right, then one slot per letter, right to left;
// the count badge and the opening hint live here too.
//
// The picture IS the question the game asks — this animal, that dish — so where
// it lives is the game's business and comes in through setTray(). Jiddo keeps
// his under img/a_<word>.webp; nothing else here knows or cares.
import {interrupt,sayLetter,sayWord,sndPop} from './audio';
import {colOf,facesOf,inkFit,markFit,unitsOf} from './letters';
import {$,restart} from './util';

// slots:false is the tracing board's tray (trace.ts): the picture alone while
// the word is being worked out on the board, and the word only once it is whole
// (revealWord), so the word never shows before the child has made it. It comes
// as one piece, joined up as the board drew it, not a slot per letter: the
// child has just watched it come together, and slots would take it apart again.
export interface TrayCfg{pic:(w:string)=>string;slots?:boolean}
let cfg:TrayCfg={pic:w=>'img/'+encodeURIComponent(w)+'.webp'};
export const setTray=(c:TrayCfg)=>{cfg=c;};
const hint=$('hint'), badge=$('badge'), wProg=$('wProg'), tray=$('tray'), prompt=$('prompt'), promptImg=$<HTMLImageElement>('promptImg');
let slotEls:HTMLElement[]=[], word:string|null=null, hintHidden=false, revealed:{w:string;pic:HTMLCanvasElement}|null=null;
export function fillSlot(i:number){slotEls[i].classList.add('filled');}
export const slotRect=(i:number)=>slotEls[i].getBoundingClientRect();
// The slot for an n-letter word, in pixels. The row is the picture at 1.5×, one
// slot per letter, and a half-slot gap between each pair: s*(1.5+1.5n) wide.
// Sizing s to fit is what keeps the slots round — left to itself the flexbox
// shrinks their width and not their height, and on a 375px phone حمار came out
// as four ovals. A short word gets fat letters and a long one lean ones, as the
// SVG game's tray did. A phone on its side is fitted to its height as well, or
// the picture alone takes a third of the screen and the field is left a strip.
// The floating letters are sized from this too (chips.ts), so the one to tap
// is always the bigger of the pair.
export const slotSize=(n:number)=>Math.min(78,innerWidth*0.94/(1.5+1.5*n),innerHeight*0.17);
// the round's word, `step` letters of it already in; null between rounds
export function buildSlots(w:string|null,step:number){
  word=w;
  for(const s of slotEls)s.remove(); slotEls=[];
  if(!w)return;
  // The board's tray never holds more than the picture and the word in one
  // piece, which is two to two and a half slots wide (and shrinks to fit,
  // style.css, when it is more), so it is sized as for two letters whatever the
  // word's length: sized for six slots, برتقال came out too small to read.
  // A hidden tab reports 0 wide, which would collapse the row to nothing; leave
  // the CSS default until resize()'s poll notices the page is back on screen.
  const u=unitsOf(w);
  if(innerWidth)tray.style.setProperty('--s',slotSize(cfg.slots===false?2:u.length).toFixed(1)+'px');
  // hidden until it is whole, and whole once it is shown — sized for the word
  // either way, so the picture does not jump when the word comes up
  if(cfg.slots===false){if(revealed?.w===w)whole(w,revealed.pic);return;}
  const f=facesOf(w);                           // each letter with its حركة on it, where it has one
  u.forEach((ch,i)=>{
    const d=document.createElement('div'), sp=document.createElement('span'), m=markFit(f[i]), k=inkFit(f[i])*m.k;
    d.className='slot'; d.style.setProperty('--c',colOf(ch)); sp.textContent=f[i]; d.appendChild(sp);
    if(k<1)d.style.setProperty('--k',k.toFixed(3));   // the slot's font is .62 of it, times this (style.css)
    if(m.dy)d.style.setProperty('--dy',m.dy.toFixed(3));   // and a letter whose mark would leave the slot moves in by this many of its ems
    d.addEventListener('pointerdown',ev=>{ev.stopPropagation();ev.preventDefault();interrupt();sayLetter(ch);sndPop(i);restart(d,'pulse');});
    tray.appendChild(d); slotEls.push(d);
    if(i<step)fillSlot(i);                      // a resize mid-word keeps its progress
  });
}
// The picture says the round's name. The round says it (sayName, round.ts):
// whether the name is still owed is its business, not the tray's.
let onPrompt=()=>{};
export function promptTaps(fn:()=>void){onPrompt=fn;}
prompt.addEventListener('pointerdown',ev=>{ev.stopPropagation();ev.preventDefault();if(!word)return;onPrompt();sndPop();restart(prompt,'pulse');});
// The picture is shown only once it has decoded; until then the tray shows a
// placeholder (the `waiting` class, style.css). An <img> keeps painting its old
// picture while the new one downloads, and the first round is dealt to whichever
// model arrived first — so on a slow connection the tortoise's word sat under
// the elephant's picture. If the picture never comes, the placeholder stays.
// `gen` is the round's number, so a slow picture cannot land on a later round.
let promptGen=0;
export function showPrompt(w:string){
  const gen=++promptGen;
  prompt.classList.add('waiting');
  promptImg.src=cfg.pic(w);
  promptImg.decode().then(()=>{if(gen===promptGen)prompt.classList.remove('waiting');},()=>{});
}
// The tracing board's word, come down into the tray for her to serve: `pic` is
// the board's own drawing of it (wordPic, trace.ts). A resize keeps it shown
// (buildSlots); the next round's word is hidden again.
export function revealWord(w:string,pic:HTMLCanvasElement){revealed={w,pic};buildSlots(w,unitsOf(w).length);}
// It stands where the slots would, no taller than they are, ringed in its first
// letter's colour as the board was; a tap on it reads it. It is the one thing in
// the tray, so it is the one that bows (bow).
function whole(w:string,pic:HTMLCanvasElement){
  const d=document.createElement('div');
  d.className='whole'; d.style.setProperty('--c',colOf(unitsOf(w)[0])); d.appendChild(pic);
  d.addEventListener('pointerdown',ev=>{ev.stopPropagation();ev.preventDefault();interrupt();sayWord(w,true);sndPop();restart(d,'pulse');});
  tray.appendChild(d); slotEls.push(d);
}
export function markNeed(i:number){slotEls.forEach((s,j)=>s.classList.toggle('need',j===i));}   // the slot that breathes; -1 for none
export function bow(){slotEls.forEach((s,i)=>setTimeout(()=>restart(s,'win'),i*70));}         // the tray takes a bow, right to left
export function setCount(n:number){wProg.textContent=String(n);restart(badge,'pop');}
export function hideHint(){if(hintHidden)return;hint.classList.add('hidden');hintHidden=true;}
